// @ts-nocheck
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]',
    );
    this.label = this.querySelector('input[type="search"] + label');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isShowingUpsell = false; // Track if showing upsell vs search results
    this.isTransitioning = false; // Prevent multiple simultaneous transitions
    this.isClicking = false; // Track if user is clicking on search results
    this.isMouseOverResults = false; // Track if mouse is over search results

    this.setupEventListeners();

    // Pre-load upsell content for optimal performance (optional)
    this.preloadUpsellContent();
  }

  preloadUpsellContent() {
    // Only preload if not already cached and this is the first instance
    if (
      !PredictiveSearch.upsellCache &&
      this.allPredictiveSearchInstances[0] === this
    ) {
      // Use setTimeout to avoid blocking the constructor
      setTimeout(() => {
        fetch('/search?section_id=predictive-search')
          .then((response) => {
            if (response.ok) {
              return response.text();
            }
            throw new Error('Preload failed');
          })
          .then((text) => {
            const upsellMarkup = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search').innerHTML;

            // Cache for all instances
            PredictiveSearch.upsellCache = upsellMarkup;
          })
          .catch(() => {
            // Silently fail - will fetch when needed
          });
      }, 100);
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Add event listener for close drawer button
    this.addEventListener('click', this.onCloseButtonClick.bind(this));

    // Add mouse event listeners to track when user is over search results
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // Close only on outside click (not on focus loss)
    this.onOutsideClickBound = this.onOutsideClick.bind(this);
    document.addEventListener('click', this.onOutsideClickBound);
  }

  disconnectedCallback() {
    if (this.onOutsideClickBound) {
      document.removeEventListener('click', this.onOutsideClickBound);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    // Prevent multiple simultaneous transitions
    if (this.isTransitioning) {
      return;
    }

    // If user starts typing, switch from upsell to search results
    if (this.isShowingUpsell && newSearchTerm.length > 0) {
      this.isShowingUpsell = false;
      // Don't clear upsell content immediately - let search results replace it smoothly
    }

    // Only remove results if it's a completely new search term (not building upon the previous one)
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Don't remove results immediately if drawer is open - let new results replace them
      if (!this.isOpen) {
        this.querySelector(
          '#predictive-search-results-groups-wrapper',
        )?.remove();
      }
    }

    // Update the search term in heading immediately as user types
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Show label only if input is truly empty (not just whitespace)
      if (!this.getQuery().length) {
        this.label?.classList.remove('hidden');
      }
      // If search term is cleared, show upsell content again
      // But only if we're not already showing upsell content
      if (!this.isShowingUpsell) {
        if (this.isOpen) {
          this.showUpsellContentInPlace();
        } else {
          this.showUpsellContent();
        }
      }
      return;
    }

    // Hide label when there's content
    this.label?.classList.add('hidden');
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  hasNoSearchResults() {
    // Check if we're showing search results but have no actual results
    if (
      this.isOpen &&
      this.getAttribute('results') === 'true' &&
      !this.isShowingUpsell
    ) {
      // Look for the no results message by checking if it exists
      const noResultsMessages = Array.from(this.querySelectorAll('h2')).filter(
        (h2) => h2.textContent.includes('Oops! Nothing matches'),
      );
      if (noResultsMessages.length > 0) {
        return true;
      }

      // Alternative check: look for products in the results
      const productsList = this.querySelector(
        '[id*="predictive-search-results-products-list"]',
      );
      const featuredList = this.querySelector(
        '[id*="predictive-search-results-featured-list"]',
      );

      // Check if we have actual product results (not just upsell)
      const hasSearchProducts =
        productsList && productsList.children.length > 0;

      // Also check if we have any search suggestions, collections, etc.
      const hasSuggestions =
        this.querySelector('#predictive-search-results-queries-list')?.children
          .length > 0;
      const hasCollections =
        this.querySelector(
          '[id*="predictive-search-results"][id*="collections-list"]',
        )?.children.length > 0;

      // If we have featured products but no search products, suggestions, or collections, it means no results
      const hasFeaturedOnly =
        featuredList && featuredList.children.length > 0 && !hasSearchProducts;

      // No results if we only have featured products (upsell) and no actual search results
      return hasFeaturedOnly && !hasSuggestions && !hasCollections;
    }
    return false;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.label?.classList.add('hidden');

    // If there's no search term, show upsell content
    if (!currentSearchTerm.length) {
      // Avoid re-rendering upsell if it's already shown
      if (
        this.isShowingUpsell &&
        this.predictiveSearchResults?.childElementCount > 0
      ) {
        this.open();
        return;
      }
      this.showUpsellContent();
      return;
    }

    // If there's a search term, proceed with normal search logic
    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Show label only if input is empty
        if (!this.getQuery().length) {
          this.label?.classList.remove('hidden');
        }
        // Do not close on focus loss; closing is handled by outside click handler
      }
    });
  }

  onOutsideClick(event) {
    if (!this.isOpen) return;
    // Close when clicking outside of the predictive-search element
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onCloseButtonClick(event) {
    // Check if the clicked element is the close button
    if (event.target.closest('.close-drawer-btn')) {
      event.preventDefault();
      event.stopPropagation();
      this.close(true); // Close and clear the search term
    } else {
      // User is clicking on search results, prevent focusout from closing
      this.isClicking = true;
      setTimeout(() => {
        this.isClicking = false;
      }, 100);
    }
  }

  onMouseEnter() {
    this.isMouseOverResults = true;
  }

  onMouseLeave() {
    this.isMouseOverResults = false;
  }

  onKeyup(event) {
    // Don't close immediately when query is empty - let onChange handle it
    // if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]',
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      // Escape special regex characters to prevent syntax errors
      const escapedPreviousTerm = previousTerm.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      );
      const matches = currentButtonText.match(
        new RegExp(escapedPreviousTerm, 'g'),
      );
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(
        new RegExp(escapedPreviousTerm, 'g'),
        newTerm,
      );
      searchForTextElement.innerText = newButtonText;
    }
  }

  updateSearchTermInHeading() {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]',
    );
    if (searchForTextElement && this.searchTerm) {
      // Update the heading with the current search term
      const currentText = searchForTextElement.innerText;
      if (currentText.includes('Best Matches For')) {
        // For search results, update the term in the heading
        const capitalizedTerm =
          this.searchTerm.charAt(0).toUpperCase() + this.searchTerm.slice(1);
        searchForTextElement.innerHTML = `Best Matches For<strong>&nbsp;${capitalizedTerm}</strong>`;
      }
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item'),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) {
      selectedOption.click();
    } else {
      // No selected option (no search results), submit the form to go to search page
      this.input.form.submit();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    // Always show loading state but don't close the drawer if it's already open
    if (!this.isOpen) {
      this.setLiveRegionLoadingState();
    } else {
      // If drawer is open, just add loading attribute without changing content
      this.setAttribute('loading', true);
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          // Only close if we're not already open with content
          if (!this.isOpen || !this.getAttribute('results')) {
            this.close();
          }
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        // Only close if we're not already open with content
        if (!this.isOpen || !this.getAttribute('results')) {
          this.close();
        }
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');

    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults.innerHTML !== resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute('results', true);
    this.isShowingUpsell = false; // Mark that we're showing search results

    // Always remove loading state
    this.removeAttribute('loading');

    // Only call setLiveRegionResults and open if we're not already open
    if (!this.isOpen) {
      this.setLiveRegionResults();
      this.open();
    } else {
      // For smooth transition when already open, just update the live region text quietly
      // Don't call setLiveRegionResults to avoid the full screen reader announcement
    }
  }

  showUpsellContent() {
    // Check static cache first - shared across all instances for optimal performance
    if (PredictiveSearch.upsellCache && PredictiveSearch.upsellCache !== null) {
      this.renderUpsellContent(PredictiveSearch.upsellCache);
      return;
    }

    // Open immediately with loading state for smooth UX
    this.openWithLoadingState();

    fetch('/search?section_id=predictive-search', {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const upsellMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // Cache the content in static property - shared across all instances
        PredictiveSearch.upsellCache = upsellMarkup;

        // Update cache in all instances for consistency
        this.allPredictiveSearchInstances.forEach((instance) => {
          // No need to set instance cache since we're using static cache
        });

        this.renderUpsellContent(upsellMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
      });
  }

  renderUpsellContent(upsellMarkup) {
    if (this.predictiveSearchResults.innerHTML !== upsellMarkup) {
      this.predictiveSearchResults.innerHTML = upsellMarkup;
    }
    this.setAttribute('results', true);
    this.isShowingUpsell = true; // Mark that we're showing upsell content

    this.setLiveRegionText('Showing recommended products and categories');

    // Smooth transition from loading to content
    this.removeAttribute('loading');
    if (!this.isOpen) {
      this.open();
    } else {
      // If already open (from loading state), transition to open state
      if (window.innerWidth >= 1260) {
        this.setAttribute('open', true);
      }
    }
  }

  showUpsellContentInPlace() {
    // Show upsell content without closing/reopening the drawer
    // This method is used when the drawer is already open
    // Don't show loading state to avoid flicker

    // Prevent multiple calls
    if (this.isTransitioning) {
      return;
    }

    // Check static cache first - instant display for optimal UX
    if (PredictiveSearch.upsellCache) {
      if (
        this.predictiveSearchResults.innerHTML !== PredictiveSearch.upsellCache
      ) {
        this.predictiveSearchResults.innerHTML = PredictiveSearch.upsellCache;
      }
      this.isShowingUpsell = true;
      return;
    }

    this.isTransitioning = true;

    fetch('/search?section_id=predictive-search', {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const upsellMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // Cache in static property for all instances
        PredictiveSearch.upsellCache = upsellMarkup;

        // Update content smoothly without replacing if identical
        if (this.predictiveSearchResults.innerHTML !== upsellMarkup) {
          this.predictiveSearchResults.innerHTML = upsellMarkup;
        }
        this.isShowingUpsell = true;
        this.isTransitioning = false;

        // Don't call setLiveRegionText to avoid any flicker
        // Don't call this.open() here since it's already open
      })
      .catch((error) => {
        this.isTransitioning = false;
        if (error?.code !== 20) {
          console.error('Error loading upsell content:', error);
        }
      });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent,
    );
  }

  getResultsMaxHeight() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isDesktop = viewportWidth >= 1260; // Desktop breakpoint
    const header = document.querySelector('.section-header');
    const headerHeight = header?.getBoundingClientRect().bottom;
    const announcementBar = document.querySelector('.announcement-bar-section');
    const announcementBarHeight = announcementBar?.offsetHeight;
    const mobileSearchBar = document.querySelector('.search-modal__content');
    const mobileSearchBarHeight = mobileSearchBar?.offsetHeight;

    if (isDesktop) {
      this.resultsMaxHeight = viewportHeight - (headerHeight + 28);
      if (this.resultsMaxHeight >= 910) {
        this.resultsMaxHeight = 910;
      }
      document.documentElement.style.setProperty(
        '--search-drawer-height',
        `${this.resultsMaxHeight}px`,
      );
    } else {
      this.setMobileSearchBarHeight(mobileSearchBarHeight);
      this.resultsMaxHeight =
        viewportHeight - (announcementBarHeight + mobileSearchBarHeight);

      // Set CSS custom property for mobile drawer height to enable scrolling
      document.documentElement.style.setProperty(
        '--mobile-search-drawer-height',
        `${this.resultsMaxHeight}px`,
      );
    }

    return this.resultsMaxHeight;
  }

  setMobileSearchBarHeight(mobileSearchBarHeight) {
    document.documentElement.style.setProperty(
      '--mobile-search-bar-height',
      `${mobileSearchBarHeight}px`,
    );
  }

  openWithLoadingState() {
    // Ensure element is in the DOM flow
    this.predictiveSearchResults.classList.remove('hidden');

    // Always calculate height to ensure mobile drawer has proper constraints
    const calculatedHeight = this.getResultsMaxHeight();

    // Set height constraint based on viewport size
    if (window.innerWidth >= 1260) {
      // Desktop: set height on main container
      this.predictiveSearchResults.style.maxHeight = `${calculatedHeight}px`;
    } else {
      // Mobile/Tablet: use mobile behavior for all devices < 1260px
      this.predictiveSearchResults.style.maxHeight = `${calculatedHeight}px`;
      document.documentElement.style.setProperty(
        '--mobile-search-drawer-height',
        `${calculatedHeight}px`,
      );
    }

    // Minimal, reliable state changes
    this.predictiveSearchResults.classList.add('is-open');
    this.setAttribute('loading', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    if (window.innerWidth >= 1260) {
      document.body.classList.add('scroll-locked');
      document.body.parentElement.classList.add('scroll-locked');
    }

    this.setLiveRegionText(
      this.loadingText || this.getAttribute('data-loading-text'),
    );
  }

  open() {
    // Ensure element is in the DOM flow
    this.predictiveSearchResults.classList.remove('hidden');

    // Always calculate height to ensure mobile drawer has proper constraints
    const calculatedHeight = this.getResultsMaxHeight();

    // Set height constraint based on viewport size
    if (window.innerWidth >= 1260) {
      // Desktop: set height on main container
      this.predictiveSearchResults.style.maxHeight = `${calculatedHeight}px`;
    } else {
      // Mobile/Tablet: use mobile behavior for all devices < 1260px
      this.predictiveSearchResults.style.maxHeight = `${calculatedHeight}px`;
      document.documentElement.style.setProperty(
        '--mobile-search-drawer-height',
        `${calculatedHeight}px`,
      );
    }

    // Minimal, reliable state changes
    this.predictiveSearchResults.classList.add('is-open');
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    if (window.innerWidth >= 1260) {
      document.body.classList.add('scroll-locked');
      document.body.parentElement.classList.add('scroll-locked');
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.isShowingUpsell = false; // Reset upsell state when closing
    this.isTransitioning = false; // Reset transition state

    // Only remove scroll-locked on desktop - mobile is handled by details-modal
    if (window.innerWidth >= 1260) {
      document.body.classList.remove('scroll-locked');
      document.body.parentElement.classList.remove('scroll-locked');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.predictiveSearchResults.classList.remove('is-open');
    this.setAttribute('open', false);
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    // Add hidden after CSS transition (desktop) or immediately (mobile)
    const addHidden = () => {
      this.predictiveSearchResults.classList.add('hidden');
    };
    if (window.innerWidth >= 1260) {
      setTimeout(addHidden, 310);
    } else {
      addHidden();
    }
  }
}

// Static cache shared across all instances for optimal performance
PredictiveSearch.upsellCache = null;

customElements.define('predictive-search', PredictiveSearch);
